import React, { useState, useEffect, useRef } from 'react';

const MusicAdminPanel = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [songs, setSongs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [musicSettings, setMusicSettings] = useState({
    pageVisible: true,
    isProOnly: false,
    defaultLanguage: 'all',
    defaultCategory: 'all',
    playerAutoplay: false,
    headerTitle: 'Darija Music',
    headerSubtitle: 'Learn through music',
    headerLogoUrl: '',
    headerAnnouncement: ''
  });
  // Local state for header form (saves on Save button click)
  const [headerForm, setHeaderForm] = useState({
    headerTitle: 'Darija Music',
    headerSubtitle: 'Learn through music',
    headerLogoUrl: '',
    headerAnnouncement: ''
  });
  const [headerSaving, setHeaderSaving] = useState(false);
  const [stats, setStats] = useState({
    totalSongs: 0,
    activeSongs: 0,
    hiddenSongs: 0,
    byLanguage: {},
    bySource: {},
    byCategory: {}
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('songs'); // songs, categories, lyricsTool
  const [showAddModal, setShowAddModal] = useState(false);

  // Lyrics Sync Tool state
  const [syncToolAudioUrl, setSyncToolAudioUrl] = useState('');
  const [syncToolLyrics, setSyncToolLyrics] = useState('');
  const [syncToolLines, setSyncToolLines] = useState([]);
  const [syncToolCurrentLine, setSyncToolCurrentLine] = useState(0);
  const [syncToolOutput, setSyncToolOutput] = useState('');
  const [syncToolPlaying, setSyncToolPlaying] = useState(false);
  const syncToolAudioRef = useRef(null);
  const [songsOrderDirty, setSongsOrderDirty] = useState(false);
  const [categoriesOrderDirty, setCategoriesOrderDirty] = useState(false);
  const [savingSongsOrder, setSavingSongsOrder] = useState(false);
  const [savingCategoriesOrder, setSavingCategoriesOrder] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    nameAr: '',
    description: '',
    icon: '🎵',
    color: '#8B5CF6',
    status: 1
  });
  const [songForm, setSongForm] = useState({
    title: '',
    artist: '',
    category: '',
    language: 'arabic',
    audioSource: 'dropbox',
    audioUrl: '',
    imageUrl: '',
    lyrics: '',
    lyricsType: 'simple',
    lyricsEnabled: true,
    duration: 0,
    status: 1,
    description: ''
  });

  const authHeaders = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };

  // Load data on mount
  useEffect(() => {
    loadSongs();
    loadCategories();
    loadSettings();
    loadStats();
  }, []);

  const loadSongs = async () => {
    try {
      const response = await fetch(`${API}/admin/music/songs`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setSongs(data.songs || []);
      }
    } catch (error) {
      console.error('Error loading songs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/music/settings`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setMusicSettings(data);
        // Sync header form with loaded settings
        setHeaderForm({
          headerTitle: data.headerTitle || 'Darija Music',
          headerSubtitle: data.headerSubtitle || 'Learn through music',
          headerLogoUrl: data.headerLogoUrl || '',
          headerAnnouncement: data.headerAnnouncement || ''
        });
      }
    } catch (error) {
      console.error('Error loading music settings:', error);
    }
  };

  // Save header settings
  const saveHeaderSettings = async () => {
    setHeaderSaving(true);
    try {
      const response = await fetch(`${API}/admin/music/settings/header`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          headerTitle: headerForm.headerTitle,
          headerSubtitle: headerForm.headerSubtitle,
          headerLogoUrl: headerForm.headerLogoUrl,
          headerAnnouncement: headerForm.headerAnnouncement
        })
      });
      if (response.ok) {
        setMusicSettings(prev => ({ ...prev, ...headerForm }));
        showNotification('Header settings saved successfully!', 'success');
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to save header settings', 'error');
      }
    } catch (error) {
      console.error('Error saving header settings:', error);
      showNotification('Error saving header settings', 'error');
    } finally {
      setHeaderSaving(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API}/admin/music/stats`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading music stats:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API}/admin/music/categories`, {
        headers: authHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (!categoryForm.name) {
        showNotification('Category name is required', 'error');
        return;
      }

      if (editingCategory) {
        const response = await fetch(`${API}/admin/music/categories/${editingCategory.id}`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify(categoryForm)
        });
        if (response.ok) {
          showNotification('Category updated successfully', 'success');
          loadCategories();
          setShowCategoryModal(false);
          setEditingCategory(null);
          resetCategoryForm();
        } else {
          showNotification('Failed to update category', 'error');
        }
      } else {
        const response = await fetch(`${API}/admin/music/categories`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(categoryForm)
        });
        if (response.ok) {
          showNotification('Category added successfully', 'success');
          loadCategories();
          setShowCategoryModal(false);
          resetCategoryForm();
        } else {
          showNotification('Failed to add category', 'error');
        }
      }
    } catch (error) {
      console.error('Error saving category:', error);
      showNotification('Error saving category', 'error');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const response = await fetch(`${API}/admin/music/categories/${categoryId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (response.ok) {
        showNotification('Category deleted successfully', 'success');
        loadCategories();
      } else {
        showNotification('Failed to delete category', 'error');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      showNotification('Error deleting category', 'error');
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      nameAr: '',
      description: '',
      icon: '🎵',
      color: '#8B5CF6',
      status: 1
    });
  };

  const openEditCategoryModal = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      nameAr: category.nameAr || '',
      description: category.description || '',
      icon: category.icon || '🎵',
      color: category.color || '#8B5CF6',
      status: category.status !== undefined ? category.status : 1
    });
    setShowCategoryModal(true);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : 'Uncategorized';
  };

  const handleSaveSong = async () => {
    try {
      if (!songForm.title || !songForm.audioUrl) {
        showNotification('Title and Audio URL are required', 'error');
        return;
      }

      if (editingSong) {
        // Update existing song
        const response = await fetch(`${API}/admin/music/songs/${editingSong.id}`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify(songForm)
        });
        if (response.ok) {
          showNotification('Song updated successfully', 'success');
          loadSongs();
          loadStats();
          loadCategories();
          setShowAddModal(false);
          setEditingSong(null);
          resetForm();
        } else {
          showNotification('Failed to update song', 'error');
        }
      } else {
        // Add new song
        const response = await fetch(`${API}/admin/music/songs`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify(songForm)
        });
        if (response.ok) {
          showNotification('Song added successfully', 'success');
          loadSongs();
          loadStats();
          loadCategories();
          setShowAddModal(false);
          resetForm();
        } else {
          showNotification('Failed to add song', 'error');
        }
      }
    } catch (error) {
      console.error('Error saving song:', error);
      showNotification('Error saving song', 'error');
    }
  };

  const handleDeleteSong = async (songId) => {
    if (!window.confirm('Are you sure you want to delete this song?')) return;

    try {
      const response = await fetch(`${API}/admin/music/songs/${songId}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (response.ok) {
        showNotification('Song deleted successfully', 'success');
        loadSongs();
        loadStats();
        loadCategories();
      } else {
        showNotification('Failed to delete song', 'error');
      }
    } catch (error) {
      console.error('Error deleting song:', error);
      showNotification('Error deleting song', 'error');
    }
  };

  const handleToggleStatus = async (song) => {
    try {
      const newStatus = song.status === 1 ? 0 : 1;
      const response = await fetch(`${API}/admin/music/songs/${song.id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        showNotification(newStatus === 1 ? 'Song activated' : 'Song hidden', 'success');
        loadSongs();
        loadStats();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleReorderSong = (songId, direction) => {
    const idx = songs.findIndex(s => s.id === songId);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= songs.length) return;

    const newSongs = [...songs];
    [newSongs[idx], newSongs[targetIdx]] = [newSongs[targetIdx], newSongs[idx]];
    setSongs(newSongs);
    setSongsOrderDirty(true);
  };

  const handleSaveSongsOrder = async () => {
    setSavingSongsOrder(true);
    try {
      const orderData = songs.map((s, i) => ({ id: s.id, order: i + 1 }));
      const response = await fetch(`${API}/admin/music/songs/reorder`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(orderData)
      });
      if (response.ok) {
        showNotification('Songs order saved!', 'success');
        setSongsOrderDirty(false);
        loadSongs();
      } else {
        showNotification('Failed to save songs order', 'error');
      }
    } catch (error) {
      console.error('Error saving songs order:', error);
      showNotification('Error saving songs order', 'error');
    } finally {
      setSavingSongsOrder(false);
    }
  };

  const handleReorderCategory = (categoryId, direction) => {
    const idx = categories.findIndex(c => c.id === categoryId);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= categories.length) return;

    const newCats = [...categories];
    [newCats[idx], newCats[targetIdx]] = [newCats[targetIdx], newCats[idx]];
    setCategories(newCats);
    setCategoriesOrderDirty(true);
  };

  const handleSaveCategoriesOrder = async () => {
    setSavingCategoriesOrder(true);
    try {
      const orderData = categories.map((c, i) => ({ id: c.id, order: i + 1 }));
      const response = await fetch(`${API}/admin/music/categories/reorder`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(orderData)
      });
      if (response.ok) {
        showNotification('Categories order saved!', 'success');
        setCategoriesOrderDirty(false);
        loadCategories();
      } else {
        showNotification('Failed to save categories order', 'error');
      }
    } catch (error) {
      console.error('Error saving categories order:', error);
      showNotification('Error saving categories order', 'error');
    } finally {
      setSavingCategoriesOrder(false);
    }
  };

  const handleUpdateSettings = async (key, value) => {
    try {
      const response = await fetch(`${API}/admin/music/settings?${key}=${value}`, {
        method: 'PUT',
        headers: authHeaders
      });
      if (response.ok) {
        setMusicSettings(prev => ({ ...prev, [key]: value }));
        showNotification('Settings updated', 'success');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const resetForm = () => {
    setSongForm({
      title: '',
      artist: '',
      category: '',
      language: 'arabic',
      audioSource: 'dropbox',
      audioUrl: '',
      imageUrl: '',
      lyrics: '',
      lyricsType: 'simple',
      lyricsEnabled: true,
      duration: 0,
      status: 1,
      description: ''
    });
  };

  const openEditModal = (song) => {
    setEditingSong(song);
    setSongForm({
      title: song.title || '',
      artist: song.artist || '',
      category: song.category || '',
      language: song.language || 'arabic',
      audioSource: song.audioSource || 'dropbox',
      audioUrl: song.audioUrl || '',
      imageUrl: song.imageUrl || '',
      lyrics: song.lyrics || '',
      lyricsType: song.lyricsType || 'simple',
      lyricsEnabled: song.lyricsEnabled !== false,
      duration: song.duration || 0,
      status: song.status !== undefined ? song.status : 1,
      description: song.description || ''
    });
    setShowAddModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🎵</span>
            <div>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Music Management
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Manage educational music content
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeSubTab === 'songs' ? (
              <button
                onClick={() => { resetForm(); setEditingSong(null); setShowAddModal(true); }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <span>➕</span> Add Song
              </button>
            ) : (
              <button
                onClick={() => { resetCategoryForm(); setEditingCategory(null); setShowCategoryModal(true); }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <span>➕</span> Add Category
              </button>
            )}
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveSubTab('songs')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeSubTab === 'songs'
                ? 'bg-blue-600 text-white'
                : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            🎶 Songs ({songs.length})
          </button>
          <button
            onClick={() => setActiveSubTab('categories')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeSubTab === 'categories'
                ? 'bg-purple-600 text-white'
                : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            📁 Categories ({categories.length})
          </button>
          <button
            onClick={() => setActiveSubTab('lyricsTool')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeSubTab === 'lyricsTool'
                ? 'bg-green-600 text-white'
                : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            ⏱️ Lyrics Sync Tool
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className="text-2xl font-bold text-blue-500">{stats.totalSongs}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Songs</div>
          </div>
          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className="text-2xl font-bold text-green-500">{stats.activeSongs}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active</div>
          </div>
          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className="text-2xl font-bold text-yellow-500">{stats.hiddenSongs}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hidden</div>
          </div>
          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className="text-2xl font-bold text-purple-500">{categories.length}</div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Categories</div>
          </div>
          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className="text-2xl font-bold text-pink-500">
              {Object.keys(stats.byLanguage).length}
            </div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Languages</div>
          </div>
        </div>
      </div>

      {/* Page Settings */}
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ⚙️ Music Page Settings
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Page Visible Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-opacity-50" style={{backgroundColor: isDarkMode ? '#374151' : '#f3f4f6'}}>
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Show in Menu</p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {musicSettings.pageVisible ? 'Music page is visible' : 'Music page is hidden'}
              </p>
            </div>
            <button
              onClick={() => handleUpdateSettings('pageVisible', !musicSettings.pageVisible)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                musicSettings.pageVisible ? 'bg-green-600' : 'bg-gray-400'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                musicSettings.pageVisible ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Pro Only Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-opacity-50" style={{backgroundColor: isDarkMode ? '#374151' : '#f3f4f6'}}>
            <div>
              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Pro Only</p>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {musicSettings.isProOnly ? 'Only paid users' : 'Free for all'}
              </p>
            </div>
            <button
              onClick={() => handleUpdateSettings('isProOnly', !musicSettings.isProOnly)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                musicSettings.isProOnly ? 'bg-purple-600' : 'bg-gray-400'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                musicSettings.isProOnly ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

        </div>

        {/* Music Page Header Settings */}
        <h4 className={`font-medium mt-6 mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          🎨 Page Header
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Title</label>
            <input
              type="text"
              value={headerForm.headerTitle}
              onChange={(e) => setHeaderForm(prev => ({ ...prev, headerTitle: e.target.value }))}
              placeholder="Darija Music"
              className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>
          <div>
            <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Subtitle</label>
            <input
              type="text"
              value={headerForm.headerSubtitle}
              onChange={(e) => setHeaderForm(prev => ({ ...prev, headerSubtitle: e.target.value }))}
              placeholder="Learn through music"
              className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>
          <div>
            <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Logo URL (Dropbox)</label>
            <input
              type="text"
              value={headerForm.headerLogoUrl}
              onChange={(e) => setHeaderForm(prev => ({ ...prev, headerLogoUrl: e.target.value }))}
              placeholder="https://www.dropbox.com/..."
              className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            />
          </div>
        </div>
        {/* Announcement - Shows above search bar */}
        <div className="mb-4">
          <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            📢 Announcement (shows above search bar)
          </label>
          <input
            type="text"
            value={headerForm.headerAnnouncement}
            onChange={(e) => setHeaderForm(prev => ({ ...prev, headerAnnouncement: e.target.value }))}
            placeholder="e.g., New songs coming soon! Check out our latest additions..."
            className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
          />
          <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Leave empty to hide the announcement bar
          </p>
        </div>
        {/* Save Header Button */}
        <button
          onClick={saveHeaderSettings}
          disabled={headerSaving}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            headerSaving
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {headerSaving ? 'Saving...' : '💾 Save Header Settings'}
        </button>
      </div>

      {/* Songs List */}
      {activeSubTab === 'songs' && (
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            🎶 Songs ({songs.length})
          </h3>
          {songsOrderDirty && (
            <button
              onClick={handleSaveSongsOrder}
              disabled={savingSongsOrder}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 font-medium"
            >
              {savingSongsOrder ? 'Saving...' : 'Save Order'}
            </button>
          )}
        </div>

        {songs.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <span className="text-4xl mb-4 block">🎵</span>
            <p>No songs yet. Click "Add Song" to get started!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {songs.map((song, songIdx) => (
              <div
                key={song.id}
                className={`flex items-center gap-4 p-4 rounded-lg border ${
                  song.status === 0
                    ? isDarkMode ? 'bg-gray-700/50 border-gray-600 opacity-60' : 'bg-gray-100 border-gray-200 opacity-60'
                    : isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}
              >
                {/* Order Arrows */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleReorderSong(song.id, 'up')}
                    disabled={songIdx === 0}
                    className={`p-1 rounded text-xs ${songIdx === 0 ? 'opacity-30 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                    title="Move up"
                  >
                    ▲
                  </button>
                  <span className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{songIdx + 1}</span>
                  <button
                    onClick={() => handleReorderSong(song.id, 'down')}
                    disabled={songIdx === songs.length - 1}
                    className={`p-1 rounded text-xs ${songIdx === songs.length - 1 ? 'opacity-30 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>

                {/* Song Image */}
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  {song.imageUrl ? (
                    <img src={song.imageUrl} alt={song.title} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-2xl text-white">🎵</span>
                  )}
                </div>

                {/* Song Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      {song.title}
                    </h4>
                  </div>
                  <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {song.artist || 'Unknown Artist'}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    {song.lyricsEnabled && (
                      <span className={`text-xs px-2 py-0.5 rounded bg-green-100 text-green-700`}>
                        Lyrics
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggleStatus(song)}
                    className={`p-2 rounded-lg ${
                      song.status === 1
                        ? 'bg-green-100 text-green-600 hover:bg-green-200'
                        : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                    }`}
                    title={song.status === 1 ? 'Active - Click to hide' : 'Hidden - Click to activate'}
                  >
                    {song.status === 1 ? '👁️' : '🙈'}
                  </button>
                  <button
                    onClick={() => openEditModal(song)}
                    className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteSong(song.id)}
                    className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Categories List */}
      {activeSubTab === 'categories' && (
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            📁 Categories ({categories.length})
          </h3>
          {categoriesOrderDirty && (
            <button
              onClick={handleSaveCategoriesOrder}
              disabled={savingCategoriesOrder}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 font-medium"
            >
              {savingCategoriesOrder ? 'Saving...' : 'Save Order'}
            </button>
          )}
        </div>

        {categories.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <span className="text-4xl mb-4 block">📁</span>
            <p>No categories yet. Click "Add Category" to get started!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {categories.map((category, catIdx) => (
              <div
                key={category.id}
                className={`flex items-center gap-4 p-4 rounded-lg border ${
                  category.status === 0
                    ? isDarkMode ? 'bg-gray-700/50 border-gray-600 opacity-60' : 'bg-gray-100 border-gray-200 opacity-60'
                    : isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}
              >
                {/* Order Arrows */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleReorderCategory(category.id, 'up')}
                    disabled={catIdx === 0}
                    className={`p-1 rounded text-xs ${catIdx === 0 ? 'opacity-30 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                    title="Move up"
                  >
                    ▲
                  </button>
                  <span className={`text-xs text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{catIdx + 1}</span>
                  <button
                    onClick={() => handleReorderCategory(category.id, 'down')}
                    disabled={catIdx === categories.length - 1}
                    className={`p-1 rounded text-xs ${catIdx === categories.length - 1 ? 'opacity-30 cursor-not-allowed' : isDarkMode ? 'hover:bg-gray-600 text-gray-300' : 'hover:bg-gray-200 text-gray-600'}`}
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>

                {/* Category Icon */}
                <span className="text-2xl flex-shrink-0" style={{ backgroundColor: category.color + '20', padding: '8px', borderRadius: '8px' }}>
                  {category.icon}
                </span>

                {/* Category Info */}
                <div className="flex-1 min-w-0">
                  <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {category.name}
                  </h4>
                  {category.nameAr && (
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {category.nameAr}
                    </p>
                  )}
                  {category.description && (
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {category.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      category.status === 1 ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {category.status === 1 ? 'Active' : 'Hidden'}
                    </span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {songs.filter(s => s.category === category.id).length} songs
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openEditCategoryModal(category)}
                    className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category.id)}
                    className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Lyrics Sync Tool */}
      {activeSubTab === 'lyricsTool' && (
      <div className={`p-6 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ⏱️ Lyrics Sync Tool
        </h3>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Easily add timestamps to your lyrics. Paste lyrics, play the song, and press the button when each line starts.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Side - Input */}
          <div className="space-y-4">
            {/* Audio URL */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Audio URL (Dropbox)
              </label>
              <input
                type="text"
                value={syncToolAudioUrl}
                onChange={(e) => setSyncToolAudioUrl(e.target.value)}
                placeholder="https://www.dropbox.com/..."
                className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>

            {/* Lyrics Input */}
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Plain Lyrics (one line per row)
              </label>
              <textarea
                value={syncToolLyrics}
                onChange={(e) => setSyncToolLyrics(e.target.value)}
                placeholder="Listen close&#10;We will learn&#10;English words&#10;In Darija terms"
                rows={10}
                className={`w-full px-3 py-2 rounded-lg border font-mono text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              />
            </div>

            {/* Prepare Button */}
            <button
              onClick={() => {
                const lines = syncToolLyrics.split('\n').filter(l => l.trim());
                setSyncToolLines(lines.map(text => ({ text, time: null })));
                setSyncToolCurrentLine(0);
                setSyncToolOutput('');
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Prepare Lyrics ({syncToolLyrics.split('\n').filter(l => l.trim()).length} lines)
            </button>
          </div>

          {/* Right Side - Sync Interface */}
          <div className="space-y-4">
            {/* Audio Player */}
            {syncToolAudioUrl && (
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <audio
                  ref={syncToolAudioRef}
                  src={syncToolAudioUrl.replace('www.dropbox.com', 'dl.dropboxusercontent.com').replace('dl=0', 'raw=1').replace('dl=1', 'raw=1')}
                  onPlay={() => setSyncToolPlaying(true)}
                  onPause={() => setSyncToolPlaying(false)}
                  className="w-full"
                  controls
                />
              </div>
            )}

            {/* Current Line to Sync */}
            {syncToolLines.length > 0 && (
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="text-center mb-4">
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Line {syncToolCurrentLine + 1} of {syncToolLines.length}
                  </span>
                  <p className={`text-xl font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {syncToolLines[syncToolCurrentLine]?.text || 'Done!'}
                  </p>
                </div>

                {syncToolCurrentLine < syncToolLines.length && (
                  <button
                    onClick={() => {
                      if (syncToolAudioRef.current) {
                        const currentTime = syncToolAudioRef.current.currentTime;
                        const minutes = Math.floor(currentTime / 60);
                        const seconds = Math.floor(currentTime % 60);
                        const timestamp = `[${minutes}:${seconds.toString().padStart(2, '0')}]`;

                        const newLines = [...syncToolLines];
                        newLines[syncToolCurrentLine].time = timestamp;
                        setSyncToolLines(newLines);

                        // Build output
                        const output = newLines
                          .filter(l => l.time)
                          .map(l => `${l.time}${l.text}`)
                          .join('\n');
                        setSyncToolOutput(output);

                        // Move to next line
                        setSyncToolCurrentLine(prev => prev + 1);
                      }
                    }}
                    className="w-full px-6 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 text-lg font-bold"
                  >
                    ⏱️ Mark Timestamp (Press when line starts)
                  </button>
                )}

                {/* Undo Button */}
                {syncToolCurrentLine > 0 && (
                  <button
                    onClick={() => {
                      const newLines = [...syncToolLines];
                      newLines[syncToolCurrentLine - 1].time = null;
                      setSyncToolLines(newLines);
                      setSyncToolCurrentLine(prev => prev - 1);

                      const output = newLines
                        .filter(l => l.time)
                        .map(l => `${l.time}${l.text}`)
                        .join('\n');
                      setSyncToolOutput(output);
                    }}
                    className="w-full mt-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    ↩️ Undo Last
                  </button>
                )}

                {/* Progress */}
                <div className="mt-4">
                  <div className="h-2 bg-gray-600 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all"
                      style={{ width: `${(syncToolCurrentLine / syncToolLines.length) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Output */}
            {syncToolOutput && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Timestamped Lyrics (Copy this)
                </label>
                <textarea
                  value={syncToolOutput}
                  readOnly
                  rows={10}
                  className={`w-full px-3 py-2 rounded-lg border font-mono text-sm ${isDarkMode ? 'bg-gray-900 border-gray-600 text-green-400' : 'bg-gray-50 border-gray-300 text-green-700'}`}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(syncToolOutput);
                    showNotification('Copied to clipboard!', 'success');
                  }}
                  className="mt-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  📋 Copy to Clipboard
                </button>
              </div>
            )}

            {/* Reset */}
            {syncToolLines.length > 0 && (
              <button
                onClick={() => {
                  setSyncToolLines([]);
                  setSyncToolCurrentLine(0);
                  setSyncToolOutput('');
                }}
                className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                🔄 Reset & Start Over
              </button>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className={`mt-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
          <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-blue-800'}`}>How to use:</h4>
          <ol className={`list-decimal list-inside space-y-1 text-sm ${isDarkMode ? 'text-gray-300' : 'text-blue-700'}`}>
            <li>Paste your Dropbox audio URL</li>
            <li>Paste your plain lyrics (one line per row)</li>
            <li>Click "Prepare Lyrics"</li>
            <li>Play the audio</li>
            <li>When each lyric line starts singing, click the green "Mark Timestamp" button</li>
            <li>Copy the timestamped output and paste it into your song's lyrics field</li>
          </ol>
        </div>
      </div>
      )}

      {/* Add/Edit Song Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`sticky top-0 p-4 border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {editingSong ? '✏️ Edit Song' : '➕ Add New Song'}
                </h3>
                <button
                  onClick={() => { setShowAddModal(false); setEditingSong(null); resetForm(); }}
                  className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Title & Artist */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={songForm.title}
                    onChange={(e) => setSongForm({ ...songForm, title: e.target.value })}
                    placeholder="Song title"
                    className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Artist
                  </label>
                  <input
                    type="text"
                    value={songForm.artist}
                    onChange={(e) => setSongForm({ ...songForm, artist: e.target.value })}
                    placeholder="Artist name"
                    className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
              </div>

              {/* Category & Language */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Category
                  </label>
                  <select
                    value={songForm.category}
                    onChange={(e) => setSongForm({ ...songForm, category: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  >
                    <option value="">-- No Category --</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Language
                  </label>
                  <select
                    value={songForm.language}
                    onChange={(e) => setSongForm({ ...songForm, language: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  >
                    <option value="arabic">🇲🇦 Arabic/Darija</option>
                    <option value="french">🇫🇷 French</option>
                    <option value="english">🇬🇧 English</option>
                    <option value="amazigh">ⵣ Amazigh</option>
                    <option value="other">🌍 Other</option>
                  </select>
                </div>
              </div>

              {/* Audio Source */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Audio Source
                </label>
                <span className={`block px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}>
                  Dropbox Only
                </span>
              </div>

              {/* Audio URL */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Audio URL *
                </label>
                <input
                  type="text"
                  value={songForm.audioUrl}
                  onChange={(e) => setSongForm({ ...songForm, audioUrl: e.target.value })}
                  placeholder="Dropbox link OR any direct .mp3 URL"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Accepts: Dropbox sharing link, OR any direct URL ending in .mp3 / .ogg / .wav / .m4a (e.g., https://example.com/song.mp3)
                </p>
              </div>

              {/* Image URL */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  📦 Cover Image URL (Dropbox)
                </label>
                <input
                  type="text"
                  value={songForm.imageUrl}
                  onChange={(e) => setSongForm({ ...songForm, imageUrl: e.target.value })}
                  placeholder="Dropbox image link (dl=1)"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Use Dropbox sharing link with dl=1 for direct image display (e.g., https://www.dropbox.com/...?dl=1)
                </p>
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  ℹ️ Song Description (What is this song about?)
                </label>
                <textarea
                  value={songForm.description}
                  onChange={(e) => setSongForm({ ...songForm, description: e.target.value })}
                  placeholder="Describe what this song teaches. E.g., 'This song teaches common Darija greetings like Hello (Salam), Good morning (Sbah lkhir), How are you (Labas), and Goodbye (Bslama).'"
                  rows={3}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  This description will be shown to users to help them understand what the song teaches.
                </p>
              </div>

              {/* Lyrics Section */}
              <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <label className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    📝 Lyrics
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={songForm.lyricsEnabled}
                        onChange={(e) => setSongForm({ ...songForm, lyricsEnabled: e.target.checked })}
                        className="rounded"
                      />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Enable lyrics
                      </span>
                    </label>
                  </div>
                </div>

                {songForm.lyricsEnabled && (
                  <>
                    <div className="mb-3">
                      <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        Lyrics Type
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="lyricsType"
                            value="simple"
                            checked={songForm.lyricsType === 'simple'}
                            onChange={() => setSongForm({ ...songForm, lyricsType: 'simple' })}
                          />
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Simple (manual scroll)
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="lyricsType"
                            value="timestamped"
                            checked={songForm.lyricsType === 'timestamped'}
                            onChange={() => setSongForm({ ...songForm, lyricsType: 'timestamped' })}
                          />
                          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Timestamped (auto-highlight)
                          </span>
                        </label>
                      </div>
                    </div>

                    <textarea
                      value={songForm.lyrics}
                      onChange={(e) => setSongForm({ ...songForm, lyrics: e.target.value })}
                      placeholder={songForm.lyricsType === 'timestamped'
                        ? '[00:00]First line here\n[00:05]Second line here\n[00:10]Third line here'
                        : 'Enter lyrics here...\nOne line per verse'
                      }
                      rows={8}
                      className={`w-full px-3 py-2 rounded-lg border font-mono text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
                    />
                    {songForm.lyricsType === 'timestamped' && (
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Format: [MM:SS]Lyrics text - e.g., [00:15]Salam alikom ya sahbi
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Duration & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Duration (seconds)
                  </label>
                  <input
                    type="number"
                    value={songForm.duration}
                    onChange={(e) => setSongForm({ ...songForm, duration: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                    className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Status
                  </label>
                  <select
                    value={songForm.status}
                    onChange={(e) => setSongForm({ ...songForm, status: parseInt(e.target.value) })}
                    className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  >
                    <option value={1}>✅ Active</option>
                    <option value={0}>🙈 Hidden</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`sticky bottom-0 p-4 border-t ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setShowAddModal(false); setEditingSong(null); resetForm(); }}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSong}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingSong ? 'Save Changes' : 'Add Song'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {editingCategory ? '✏️ Edit Category' : '➕ Add New Category'}
                </h3>
                <button
                  onClick={() => { setShowCategoryModal(false); setEditingCategory(null); resetCategoryForm(); }}
                  className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Name (English) *
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="Category name"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              {/* Arabic Name */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Name (Arabic)
                </label>
                <input
                  type="text"
                  value={categoryForm.nameAr}
                  onChange={(e) => setCategoryForm({ ...categoryForm, nameAr: e.target.value })}
                  placeholder="اسم التصنيف"
                  dir="rtl"
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              {/* Icon & Color */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Icon (Emoji)
                  </label>
                  <input
                    type="text"
                    value={categoryForm.icon}
                    onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                    placeholder="🎵"
                    className={`w-full px-3 py-2 rounded-lg border text-center text-2xl ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Color
                  </label>
                  <input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    className="w-full h-10 rounded-lg border cursor-pointer"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description
                </label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Category description"
                  rows={2}
                  className={`w-full px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                />
              </div>

              {/* Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="categoryStatus"
                  checked={categoryForm.status === 1}
                  onChange={(e) => setCategoryForm({ ...categoryForm, status: e.target.checked ? 1 : 0 })}
                  className="rounded"
                />
                <label htmlFor="categoryStatus" className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Active (visible to users)
                </label>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={() => { setShowCategoryModal(false); setEditingCategory(null); resetCategoryForm(); }}
                  className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCategory}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingCategory ? 'Save Changes' : 'Add Category'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicAdminPanel;
